/**
 * registrationRequestMatch — which pending registration request, if any, does
 * this student correspond to?
 *
 * WHY THIS IS A GUESS AND MUST STAY ONE (card wW6lKRdSTxE4uvp1Vld3, step 2).
 * A `registrationRequests` child carries `childName` — a name a parent typed
 * into a public form — and nothing else that identifies a student. The link to
 * a `students` doc (`child.assignedStudentId`) is written BY the assign, so
 * before the assign there is no stored link to read. Matching therefore has
 * exactly one signal available: the name.
 *
 * That is why this module ranks candidates instead of resolving one, and why
 * `pickDefaultMatch` refuses to choose in every case except the single
 * unambiguous one. Advancing the wrong family's registration is worse than
 * leaving a ghost row for an admin to clear by hand: the ghost is visible and
 * reversible, a request silently marked `placed` for the wrong child is
 * neither. The UI shows the parent name and submission date next to every
 * candidate for the same reason — the human confirms, the matcher only offers.
 *
 * Deliberately pure: no Firestore, no React. The caller passes the request docs
 * it already has.
 */

/** Lowercase, strip punctuation, collapse whitespace. '  Cillian  Murphy ' → 'cillian murphy' */
function normalizeName(value) {
	if (typeof value !== 'string') return '';
	return value
		.toLowerCase()
		.replace(/[.,'’`-]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function firstToken(normalized) {
	return normalized.split(' ')[0] || '';
}

/** Tolerant timestamp → ms. Handles Firestore Timestamp, {seconds}, Date, number. */
function toMillis(value) {
	if (!value) return 0;
	if (typeof value.toMillis === 'function') return value.toMillis();
	if (typeof value.toDate === 'function') return value.toDate().getTime();
	if (typeof value.seconds === 'number') return value.seconds * 1000;
	if (value instanceof Date) return value.getTime();
	if (typeof value === 'number') return value;
	return 0;
}

/** The name a student is known by, from either shape the app stores. */
export function studentDisplayName(student) {
	if (!student) return '';
	return (
		student.fullName ||
		[student.firstName, student.lastName].filter(Boolean).join(' ') ||
		''
	);
}

// A request is worth offering only while it is still being worked. 'placed'
// means every child already has a slot; 'archived' is a closed request. Both
// would produce a link that advances nothing.
const LIVE_REQUEST_STATUSES = ['new', 'worked'];

/**
 * Candidate (requestId, childId) pairs this student might be.
 *
 * @param {Array} requests  registrationRequests docs ({ id, status, parent, children, createdAt })
 * @param {Object} student  a students doc
 * @returns {Array} matches, exact first then partial, each newest-request first
 */
export function findRequestMatchesForStudent({ requests, student } = {}) {
	const studentName = normalizeName(studentDisplayName(student));
	if (!studentName || !Array.isArray(requests)) return [];
	const studentFirst = firstToken(studentName);

	const matches = [];
	for (const request of requests) {
		if (!request || !LIVE_REQUEST_STATUSES.includes(request.status)) continue;
		const children = Array.isArray(request.children) ? request.children : [];
		for (const child of children) {
			if (!child || !child.childId) continue;
			// Already placed — linking again would re-stamp a slot it does not own.
			if (child.assignedSlotId) continue;

			const childName = normalizeName(child.childName);
			if (!childName) continue;

			let confidence = null;
			if (childName === studentName) confidence = 'exact';
			else if (studentFirst && firstToken(childName) === studentFirst) confidence = 'partial';
			if (!confidence) continue;

			matches.push({
				requestId: request.id,
				childId: child.childId,
				childName: child.childName,
				parentName: (request.parent && request.parent.name) || '',
				createdAtMs: toMillis(request.createdAt),
				confidence,
			});
		}
	}

	return matches.sort((a, b) => {
		if (a.confidence !== b.confidence) return a.confidence === 'exact' ? -1 : 1;
		return b.createdAtMs - a.createdAtMs;
	});
}

/**
 * The one match safe to pre-select, or null to make the human choose.
 *
 * ONE exact match and nothing else. Two exact matches means two children share
 * a name and only a person can tell them apart; a partial match ('Cillian' vs
 * 'Cillian Murphy') is a first-name collision away from being wrong, and
 * siblings share a last name, so partials are offered and never assumed.
 */
export function pickDefaultMatch(matches) {
	if (!Array.isArray(matches) || matches.length !== 1) return null;
	return matches[0].confidence === 'exact' ? matches[0] : null;
}

/** Stable identity for a match, for radio values and selection state. */
export function matchKey(match) {
	return match ? `${match.requestId}:${match.childId}` : '';
}
